/**
 * La file des annonces : ce qui reste à l'écran, et combien de temps.
 *
 * Module pur, avec le temps **injecté**. Deux raisons, et la seconde est la vraie :
 * on peut tester le vieillissement sans attendre, et surtout l'horloge ne doit pas
 * décider seule — en ligne, l'état arrive par paquets, et une file qui se viderait
 * sur une horloge indépendante avalerait des annonces jamais lues.
 */

import type { Annonce } from "./annonces.js";

/** Au-delà, l'écran est couvert : on garde les plus récentes. */
const MAX_VISIBLES = 3;

/**
 * Identifiants mémorisés pour ne rien remontrer.
 *
 * Borné : l'état est repoussé plusieurs fois par seconde, et un `Set` qui grandit
 * sans fin sur une soirée entière est une fuite — discrète, mais une fuite.
 */
const MAX_MEMOIRE = 200;

interface Affichee {
  readonly annonce: Annonce;
  /** Instant, en secondes, où elle disparaît. */
  readonly jusqua: number;
}

export interface Fil {
  readonly visibles: readonly Annonce[];
  readonly vues: ReadonlySet<string>;
  /** Détail interne : ce qui est affiché, avec sa date de péremption. */
  readonly affichees: readonly Affichee[];
}

export function filVide(): Fil {
  return { visibles: [], vues: new Set<string>(), affichees: [] };
}

/** Ajoute ce qui n'a jamais été montré, et rafraîchit ce qui est encore à l'écran. */
export function ajouter(
  fil: Fil, annonces: readonly Annonce[], maintenant: number,
): Fil {
  const neuves = annonces.filter((a) => !fil.vues.has(a.id));
  if (neuves.length === 0) return expirer(fil, maintenant);

  const vues = new Set(fil.vues);
  for (const a of neuves) vues.add(a.id);

  const affichees = [
    ...vivantes(fil.affichees, maintenant),
    ...neuves.map((annonce) => ({ annonce, jusqua: maintenant + annonce.duree })),
  ].slice(-MAX_VISIBLES);

  return {
    affichees,
    visibles: affichees.map((a) => a.annonce),
    vues: borner(vues),
  };
}

/** Retire ce dont le temps est passé. */
export function expirer(fil: Fil, maintenant: number): Fil {
  const affichees = vivantes(fil.affichees, maintenant);
  if (affichees.length === fil.affichees.length) return fil;
  return { ...fil, affichees, visibles: affichees.map((a) => a.annonce) };
}

function vivantes(
  affichees: readonly Affichee[], maintenant: number,
): readonly Affichee[] {
  return affichees.filter((a) => a.jusqua > maintenant);
}

/** Un `Set` conserve l'ordre d'insertion : les plus anciens partent d'abord. */
function borner(vues: Set<string>): ReadonlySet<string> {
  if (vues.size <= MAX_MEMOIRE) return vues;
  const garder = [...vues].slice(-MAX_MEMOIRE);
  return new Set(garder);
}
